import type { Bulle } from './api/types';

/**
 * Ce que les touches de la carte changent au dessin des bulles (chantier modes-de-la-carte,
 * modification 02), passé par la feuille à la Carte et à la Hiérarchie : ce qu'une bulle dit à la
 * place de son titre, et si la recherche l'a trouvée. Le calcul est dans `projets/` ; la Carte et
 * la Hiérarchie ne font que l'afficher.
 */
export interface LoupeDeLaCarte {
  /**
   * Une touche d'info enfoncée : ce que la bulle dit à la place de son titre, ou `null` (elle n'a
   * pas l'info : son titre, pâli). Aucune touche : `null`, les titres.
   */
  infoDe: ((bulle: Bulle) => string | null) | null;
  /** La recherche ouverte, quelque chose de tapé : la bulle est-elle trouvée ? Sinon `null`. */
  trouvee: ((bulle: Bulle) => boolean) | null;
  /**
   * Un contact choisi dans le répertoire (marge R, modification 03), ou en Analyse une ligne du
   * panneau survolée, une bulle cliquée (theme-analyse, ticket 02) : ses bulles s'allument.
   */
  allumees: ReadonlySet<string> | null;
  /**
   * En Analyse (chantier theme-analyse, ticket 02) : ce que dit chaque bulle sous son titre (sa
   * durée) et dans son coin (J-n), et ce que dit le centre (le temps qui reste). `null` : un autre mode.
   */
  analyse: {
    deLaBulle: (bulle: Bulle) => BulleEnAnalyse | null;
    duCentre: string | null;
  } | null;
}

/** Une bulle en Analyse : sa durée en mots, son compte à rebours, et si elle luit (30 jours ou moins). */
export interface BulleEnAnalyse {
  duree: string | null;
  rebours: string | null;
  lueur: 'proche' | 'bientot' | null;
}

/** Rien d'enfoncé, rien de cherché : les bulles comme toujours. */
export const SANS_LOUPE: LoupeDeLaCarte = Object.freeze({ infoDe: null, trouvee: null, allumees: null, analyse: null });

/** Ce qu'un nœud reçoit de la loupe. */
export interface LoupeSurUneBulle {
  info: string | null;
  sansInfo: boolean;
  terne: boolean;
  allumee: boolean;
  /** En Analyse seulement. */
  analyse?: BulleEnAnalyse | null;
}

/** Ce qu'un nœud reçoit de la loupe : le texte à la place du titre, et s'il pâlit. */
export function loupeSurLaBulle(
  loupe: LoupeDeLaCarte,
  bulle: Bulle
): LoupeSurUneBulle {
  const info = loupe.infoDe ? loupe.infoDe(bulle) : null;
  return {
    info,
    sansInfo: loupe.infoDe !== null && info === null,
    terne: loupe.trouvee !== null && !loupe.trouvee(bulle),
    allumee: loupe.allumees?.has(bulle.id) ?? false,
    analyse: loupe.analyse?.deLaBulle(bulle) ?? null,
  };
}
